import React from 'react';
import { theme, fontStack, bodyFont } from '../theme';
import { FadeIn, Divider, CrossIcon } from '../components/shared';
import churchConfig from '../churchConfig';
import { useChurchData, useChurchInfo } from '../data/ChurchDataProvider';

export default function AboutPage() {
  const { data } = useChurchData();
  const info = useChurchInfo();
  // Core values from the strategic plan — official six pillars
  const coreValueDetails = [
    { icon: "🌍", title: "Missions / Evangelism", desc: "Reaching our community and the world with the Gospel — from the Annie Armstrong Easter Offering to Grant Parish outreach and beyond." },
    { icon: "🎵", title: "Worship", desc: "Glorifying God through heartfelt praise, hymns, and music that draws us into His presence every time we gather." },
    { icon: "🤝", title: "Ministry", desc: "Serving one another and our neighbors through Celebrate Recovery, the Clothes Closet, Prayer Quilts, and caring for those in need." },
    { icon: "❤️", title: "Fellowship", desc: "Building authentic community — from Sunday morning fellowship to potluck dinners, where strangers become family." },
    { icon: "📖", title: "Discipleship", desc: "Growing deeper in Christ through Sunday School, Wednesday Bible Study, R.A.'s, G.A.'s, Mission Friends, and Youth Ministry." },
    { icon: "🙏", title: "Prayer", desc: "The foundation of everything we do — from Monday Men's Prayer to the Deacon's Prayer Schedule and our intercessory prayer ministry." },
  ];

  // Timeline milestones
  const timeline = [
    { year: "1861", event: "Early settlers of an area known as Prospect organized a Baptist church and gave it the simple name \"Prospect Church.\"" },
    { year: "1860s–1920s", event: "Churchgoers traveled by horseback, buggy, or wagon over twisted paths of least resistance to attend once-a-month services together." },
    { year: "Early 1900s", event: "The church disbanded and later restarted with the new name \"New Prospect Baptist Church.\"" },
    { year: "1924", event: "Twenty-nine people enrolled and signed the covenant on October 5, 1924, establishing the foundation we build on today." },
    { year: "Present", event: "Under the leadership of Pastor Trey Lewis, NPBC continues its mission of making believers out of unbelievers from our home in Dry Prong, Louisiana." },
  ];

  return (
    <div style={{ paddingTop: 100 }}>
      {/* Header */}
      <section style={{ padding: "80px 24px 60px", textAlign: "center" }}>
        <FadeIn>
          <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 16 }}>About Us</div>
          <h1 style={{ fontFamily: fontStack, fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 300, color: theme.cream, lineHeight: 1.1 }}>
            Our <span style={{ fontStyle: "italic", fontWeight: 600, color: theme.gold }}>Story</span>
          </h1>
          <Divider width={60} />
          <p style={{ fontFamily: bodyFont, fontSize: 16, color: theme.textMuted, maxWidth: 600, margin: "16px auto 0", lineHeight: 1.7 }}>
            Over 160 years of faithfulness — from a frontier gathering under the pines to a thriving church family in Dry Prong, Louisiana.
          </p>
        </FadeIn>
      </section>

      {/* ── FOUNDING HISTORY ── */}
      <section style={{ padding: "0 24px 80px" }}>
        <FadeIn>
          <div style={{ maxWidth: 850, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 48 }}>
            <div>
              <h3 style={{ fontFamily: fontStack, fontSize: 28, fontWeight: 500, color: theme.cream, marginBottom: 16 }}>A Legacy Since 1861</h3>
              <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.textMuted, lineHeight: 1.8, marginBottom: 16 }}>
                In 1861, the early settlers of an area known as Prospect organized a Baptist church and gave it the simple name "Prospect Church." Those first churchgoers traveled by horseback, buggy, or wagon over twisted paths of least resistance to attend once-a-month services together.
              </p>
              <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.textMuted, lineHeight: 1.8, marginBottom: 16 }}>
                Though the church later disbanded, it was restarted with the new name "New Prospect Baptist Church." On October 5, 1924, twenty-nine faithful people enrolled and signed the covenant — laying the foundation that we continue to build upon today.
              </p>
              <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, fontStyle: "italic", lineHeight: 1.6, opacity: 0.7 }}>
                — From Jimmie Ruth Coleman, <em>A Church with A Vision: As Seen Through My Eyes</em>, 2010
              </p>
            </div>
            <div>
              <h3 style={{ fontFamily: fontStack, fontSize: 28, fontWeight: 500, color: theme.cream, marginBottom: 16 }}>Our Mission & Vision</h3>
              <div style={{
                padding: 24, marginBottom: 20,
                background: `linear-gradient(135deg, rgba(200,164,86,0.08) 0%, ${theme.bgCard} 100%)`,
                border: `1px solid ${theme.borderHover}`,
              }}>
                <div style={{ fontFamily: bodyFont, fontSize: 10, fontWeight: 600, color: theme.gold, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 8 }}>Mission Statement</div>
                <p style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, fontStyle: "italic", lineHeight: 1.3 }}>
                  "{info.mission}"
                </p>
              </div>
              <div style={{
                padding: 24,
                background: theme.bgCard, border: `1px solid ${theme.border}`,
              }}>
                <div style={{ fontFamily: bodyFont, fontSize: 10, fontWeight: 600, color: theme.gold, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 8 }}>Vision Statement</div>
                <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.textMuted, lineHeight: 1.7 }}>
                  {info.vision}
                </p>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── TIMELINE ── */}
      <section style={{ padding: "80px 24px", background: `linear-gradient(180deg, ${theme.bg} 0%, ${theme.accent} 50%, ${theme.bg} 100%)` }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 50 }}>
              <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Our Journey</div>
              <h2 style={{ fontFamily: fontStack, fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 400, color: theme.cream }}>Through the Years</h2>
              <Divider />
            </div>
          </FadeIn>

          <div style={{ position: "relative", paddingLeft: 40 }}>
            {/* Vertical line */}
            <div style={{
              position: "absolute", left: 11, top: 8, bottom: 8, width: 1,
              background: `linear-gradient(180deg, ${theme.gold}, ${theme.border}, transparent)`,
            }} />

            {timeline.map((item, i) => (
              <FadeIn key={i} delay={i * 0.12}>
                <div style={{ position: "relative", marginBottom: 36 }}>
                  {/* Dot */}
                  <div style={{
                    position: "absolute", left: -40, top: 6,
                    width: 22, height: 22, borderRadius: "50%",
                    background: theme.bg, border: `2px solid ${theme.gold}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: theme.gold }} />
                  </div>
                  <div style={{ fontFamily: fontStack, fontSize: 20, fontWeight: 600, color: theme.gold, marginBottom: 6 }}>{item.year}</div>
                  <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.textMuted, lineHeight: 1.7 }}>{item.event}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── CORE VALUES (from Strategic Plan) ── */}
      <section style={{ padding: "80px 24px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 60 }}>
              <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>What Guides Us</div>
              <h2 style={{ fontFamily: fontStack, fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 400, color: theme.cream }}>Our Core Values</h2>
              <Divider />
              <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.textMuted, maxWidth: 520, margin: "12px auto 0", lineHeight: 1.7 }}>
                Six pillars that define who we are and how we carry out our mission of making believers out of unbelievers.
              </p>
            </div>
          </FadeIn>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
            {coreValueDetails.map((v, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div style={{ padding: 32, background: theme.bgCard, border: `1px solid ${theme.border}`, transition: "all 0.4s ease", textAlign: "center" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = theme.gold; e.currentTarget.style.transform = "translateY(-4px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <div style={{ fontSize: 32, marginBottom: 16 }}>{v.icon}</div>
                  <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 12 }}>{v.title}</h3>
                  <p style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, lineHeight: 1.7 }}>{v.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT WE BELIEVE ── */}
      <section style={{ padding: "80px 24px", background: `linear-gradient(180deg, ${theme.bg} 0%, ${theme.accent} 50%, ${theme.bg} 100%)` }}>
        <FadeIn>
          <div style={{ maxWidth: 750, margin: "0 auto", textAlign: "center" }}>
            <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Our Foundation</div>
            <h2 style={{ fontFamily: fontStack, fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 400, color: theme.cream, marginBottom: 12 }}>What We Believe</h2>
            <Divider />
            <div style={{ textAlign: "left", marginTop: 32, display: "grid", gap: 24 }}>
              {[
                { title: "The Bible", text: "We believe the Holy Bible is God's inspired, infallible Word — the supreme authority for faith and practice in our lives." },
                { title: "God", text: "We believe in one God, eternally existing in three persons: Father, Son, and Holy Spirit." },
                { title: "Salvation", text: "We believe salvation is a gift of God's grace, received through faith in Jesus Christ alone — not by works, but by His finished work on the cross." },
                { title: "Baptism & The Lord's Supper", text: "As a Baptist church, we practice believer's baptism by immersion and regularly observe the Lord's Supper as an act of remembrance and worship." },
                { title: "The Great Commission", text: "We are committed to the call of Matthew 28:19-20 — to go and make disciples of all nations, baptizing and teaching them to obey all that Christ commanded." },
              ].map((b, i) => (
                <div key={i} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <CrossIcon size={18} color={theme.gold} />
                  <div>
                    <h4 style={{ fontFamily: fontStack, fontSize: 18, fontWeight: 500, color: theme.cream, marginBottom: 4 }}>{b.title}</h4>
                    <p style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, lineHeight: 1.7 }}>{b.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── SCRIPTURE INSPIRATION (from strategic plan) ── */}
      <section style={{ padding: "80px 24px" }}>
        <FadeIn>
          <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
            <div style={{
              padding: 40,
              background: `linear-gradient(135deg, rgba(200,164,86,0.06) 0%, ${theme.bgCard} 100%)`,
              border: `1px solid ${theme.border}`,
            }}>
              <div style={{ fontFamily: bodyFont, fontSize: 10, fontWeight: 600, color: theme.gold, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 16 }}>Philippians 3:13–14</div>
              <p style={{ fontFamily: fontStack, fontSize: "clamp(20px, 3.5vw, 28px)", fontWeight: 300, color: theme.cream, lineHeight: 1.4, fontStyle: "italic" }}>
                "I press on to reach the end of the race and receive the heavenly prize for which God, through Christ Jesus, is calling us."
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── CHURCH STAFF ── */}
      <section style={{ padding: "80px 24px", background: `linear-gradient(180deg, ${theme.bg} 0%, ${theme.accent} 50%, ${theme.bg} 100%)` }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 60 }}>
              <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Our Team</div>
              <h2 style={{ fontFamily: fontStack, fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 400, color: theme.cream }}>Church Staff</h2>
              <Divider />
            </div>
          </FadeIn>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 32 }}>
            {data.leaders.map((l, i) => (
              <FadeIn key={l.id || i} delay={i * 0.1}>
                <div style={{ textAlign: "center", padding: 24 }}>
                  <div style={{ width: 100, height: 100, borderRadius: "50%", background: l.photo ? `url(${l.photo}) center/cover` : `linear-gradient(135deg, ${theme.accent}, ${theme.bgCard})`, border: `2px solid ${theme.border}`, margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    {!l.photo && <CrossIcon size={28} color={theme.goldDark} />}
                  </div>
                  <div style={{ fontFamily: bodyFont, fontSize: 10, fontWeight: 500, color: theme.gold, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 6 }}>{l.role}</div>
                  <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 6 }}>{l.name}</h3>
                  <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, lineHeight: 1.6 }}>{l.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── MINISTRIES ── */}
      <section style={{ padding: "80px 24px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 60 }}>
              <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Get Involved</div>
              <h2 style={{ fontFamily: fontStack, fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 400, color: theme.cream }}>Our Ministries</h2>
              <Divider />
            </div>
          </FadeIn>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            {data.ministries.map((m, i) => (
              <FadeIn key={m.id || i} delay={i * 0.08}>
                <div style={{ padding: 28, background: theme.bgCard, border: `1px solid ${theme.border}`, transition: "all 0.4s ease" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = theme.gold; e.currentTarget.style.transform = "translateY(-3px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                    <span style={{ fontSize: 24 }}>{m.icon}</span>
                    <h3 style={{ fontFamily: fontStack, fontSize: 20, fontWeight: 500, color: theme.cream }}>{m.name}</h3>
                  </div>
                  <p style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, lineHeight: 1.7 }}>{m.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
